import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

// Import related entities for cascading updates
import { PersonFinal } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { MomentumScore } from '@/api/entities';

export default function EntityManagementTable({ entity, entityName, columns }) {
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [updateErrors, setUpdateErrors] = useState([]);
    const { toast } = useToast();

    useEffect(() => {
        const fetchRecords = async () => {
            setIsLoading(true);
            try {
                const data = await entity.list();
                setRecords(data);
            } catch (error) {
                toast({ title: `Failed to load ${entityName}`, description: error.message, variant: "destructive" });
            }
            setIsLoading(false);
        };
        fetchRecords();
    }, [entity, entityName, toast]);

    // Helper function to clean invalid data in a record
    const cleanRecordData = (record) => {
        const cleaned = { ...record };
        
        // Fix procurement_friendliness field if it's numeric
        if (cleaned.procurement_friendliness && typeof cleaned.procurement_friendliness === 'number') {
            // Map numeric values to valid enum strings
            const numericValue = cleaned.procurement_friendliness;
            if (numericValue >= 80) {
                cleaned.procurement_friendliness = 'Friendly';
            } else if (numericValue >= 60) {
                cleaned.procurement_friendliness = 'Standard';
            } else if (numericValue >= 40) {
                cleaned.procurement_friendliness = 'Bureaucratic';
            } else {
                cleaned.procurement_friendliness = 'Monitor';
            }
        }
        
        // Ensure procurement_friendliness is a valid enum value
        const validProcurementValues = ['Friendly', 'Standard', 'Bureaucratic', 'Monitor'];
        if (cleaned.procurement_friendliness && !validProcurementValues.includes(cleaned.procurement_friendliness)) {
            cleaned.procurement_friendliness = 'Standard'; // Default fallback
        }
        
        return cleaned;
    };

    // Helper function to cascade visibility updates for accounts
    const cascadeAccountVisibility = async (accountNames, isVisible) => {
        const cascadeErrors = [];
        let cascadeSuccesses = 0;

        if (!isVisible) { // Only cascade when hiding accounts
            try {
                // Get all related records for these accounts
                const [people, personaActivities, momentumScores] = await Promise.all([
                    PersonFinal.list(), // Get all people to filter by account_name
                    PersonaActivity.list(), // Get all persona activities 
                    MomentumScore.list() // Get all momentum scores to filter by institution_name
                ]);

                // Filter and update related People records
                const relatedPeople = people.filter(person => 
                    accountNames.includes(person.account_name)
                );

                for (const person of relatedPeople) {
                    try {
                        const cleanedPerson = cleanRecordData(person);
                        await PersonFinal.update(person.id, { ...cleanedPerson, is_visible: false });
                        cascadeSuccesses++;
                    } catch (error) {
                        cascadeErrors.push(`Person: ${person.first_name} ${person.last_name}`);
                    }
                }

                // Filter and update related PersonaActivity records
                const relatedActivities = personaActivities.filter(activity => {
                    // Match by account name through people records or direct account reference
                    const matchingPerson = people.find(p => 
                        (p.person_id && p.person_id === activity.person_id) || 
                        (p.first_name === activity.first_name && p.last_name === activity.last_name)
                    );
                    return matchingPerson && accountNames.includes(matchingPerson.account_name);
                });

                for (const activity of relatedActivities) {
                    try {
                        const cleanedActivity = cleanRecordData(activity);
                        await PersonaActivity.update(activity.id, { ...cleanedActivity, is_visible: false });
                        cascadeSuccesses++;
                    } catch (error) {
                        cascadeErrors.push(`Activity: ${activity.full_name || `${activity.first_name} ${activity.last_name}`}`);
                    }
                }

                // Filter and update related MomentumScore records
                const relatedMomentum = momentumScores.filter(score => 
                    accountNames.includes(score.institution_name)
                );

                for (const momentum of relatedMomentum) {
                    try {
                        const cleanedMomentum = cleanRecordData(momentum);
                        await MomentumScore.update(momentum.id, { ...cleanedMomentum, is_visible: false });
                        cascadeSuccesses++;
                    } catch (error) {
                        cascadeErrors.push(`Momentum: ${momentum.institution_name}`);
                    }
                }

            } catch (error) {
                console.error("Error in cascade update:", error);
                cascadeErrors.push("Failed to fetch related records");
            }
        }

        return { cascadeSuccesses, cascadeErrors };
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(new Set(records.map(r => r.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectOne = (id, checked) => {
        const newSelectedIds = new Set(selectedIds);
        if (checked) {
            newSelectedIds.add(id);
        } else {
            newSelectedIds.delete(id);
        }
        setSelectedIds(newSelectedIds);
    };

    const handleBulkUpdate = async (isVisible) => {
        if (selectedIds.size === 0) {
            toast({ title: "No records selected", description: "Please select records to update.", variant: "destructive" });
            return;
        }

        setIsUpdating(true);
        setUpdateErrors([]);
        const errors = [];
        const successfulUpdates = [];

        // Process updates one by one to handle individual errors
        for (const id of selectedIds) {
            try {
                // Fetch the current record first
                const currentRecord = await entity.get(id);
                
                // Clean the record data to fix validation issues
                const cleanedRecord = cleanRecordData(currentRecord);
                
                // Update with cleaned data and new visibility
                const updatedData = {
                    ...cleanedRecord,
                    is_visible: isVisible
                };
                
                await entity.update(id, updatedData);
                successfulUpdates.push(id);
            } catch (error) {
                console.error(`Failed to update record ${id}:`, error);
                const record = records.find(r => r.id === id);
                const recordName = record ? getRecordDisplayName(record) : id;
                errors.push({
                    id,
                    name: recordName,
                    error: error.message || 'Unknown error'
                });
            }
        }

        // If this is AccountFinal entity and we're hiding accounts, cascade the visibility
        let cascadeResults = { cascadeSuccesses: 0, cascadeErrors: [] };
        if (entityName === "Accounts" && !isVisible && successfulUpdates.length > 0) {
            const updatedAccountNames = successfulUpdates
                .map(id => records.find(r => r.id === id))
                .filter(record => record && record.account_name)
                .map(record => record.account_name);
            
            if (updatedAccountNames.length > 0) {
                cascadeResults = await cascadeAccountVisibility(updatedAccountNames, isVisible);
            }
        }

        // Update local state for successful updates
        if (successfulUpdates.length > 0) {
            setRecords(prevRecords => 
                prevRecords.map(rec => 
                    successfulUpdates.includes(rec.id) ? { ...rec, is_visible: isVisible } : rec
                )
            );
        }

        // Show results with cascade information
        if (errors.length === 0) {
            const baseMessage = `${successfulUpdates.length} ${entityName.toLowerCase()} updated successfully.`;
            const cascadeMessage = cascadeResults.cascadeSuccesses > 0 
                ? ` Also updated ${cascadeResults.cascadeSuccesses} related records.`
                : "";
            
            toast({
                title: "Bulk Update Complete",
                description: baseMessage + cascadeMessage
            });
        } else {
            const cascadeMessage = cascadeResults.cascadeSuccesses > 0 
                ? ` ${cascadeResults.cascadeSuccesses} related records also updated.`
                : "";
                
            toast({
                title: "Bulk Update Completed with Errors",
                description: `${successfulUpdates.length} records updated successfully. ${errors.length} failed.${cascadeMessage}`,
                variant: "destructive"
            });
            setUpdateErrors([...errors, ...cascadeResults.cascadeErrors.map(err => ({ 
                id: 'cascade', 
                name: 'Cascade Update', 
                error: err 
            }))]);
        }

        setSelectedIds(new Set());
        setIsUpdating(false);
    };

    // Helper function to get display name for a record
    const getRecordDisplayName = (record) => {
        if (record.account_name) return record.account_name;
        if (record.institution_name) return record.institution_name;
        if (record.title) return record.title;
        if (record.first_name && record.last_name) return `${record.first_name} ${record.last_name}`;
        if (record.full_name) return record.full_name;
        return record.id;
    };

    const isAllSelected = useMemo(() => records.length > 0 && selectedIds.size === records.length, [records, selectedIds]);

    if (isLoading) {
        return <Skeleton className="h-96 w-full" />;
    }

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Button size="sm" onClick={() => handleBulkUpdate(true)} disabled={isUpdating || selectedIds.size === 0}>
                    {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                    Set Visible ({selectedIds.size})
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleBulkUpdate(false)} disabled={isUpdating || selectedIds.size === 0}>
                    {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <EyeOff className="mr-2 h-4 w-4" />}
                    Set Hidden ({selectedIds.size})
                </Button>
                {entityName === "Accounts" && (
                    <p className="text-xs text-gray-500 ml-2">
                        Note: Hiding accounts will also hide related people, activities, and momentum scores.
                    </p>
                )}
            </div>

            {/* Error Summary */}
            {updateErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        Update Errors ({updateErrors.length})
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                        {updateErrors.map((error, index) => (
                            <div key={index} className="text-sm text-red-700">
                                <span className="font-medium">{error.name}:</span> {error.error}
                            </div>
                        ))}
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 text-red-700 border-red-300 hover:bg-red-100"
                        onClick={() => setUpdateErrors([])}
                    >
                        Dismiss
                    </Button>
                </div>
            )}

            <ScrollArea className="h-[60vh] border rounded-lg">
                <Table>
                    <TableHeader className="sticky top-0 bg-gray-50 z-10">
                        <TableRow>
                            <TableHead className="w-[50px]">
                                <Checkbox
                                    checked={isAllSelected}
                                    onCheckedChange={handleSelectAll}
                                />
                            </TableHead>
                            <TableHead className="w-[120px]">Visibility</TableHead>
                            {columns.map(col => <TableHead key={col.key}>{col.header}</TableHead>)}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {records.map(record => (
                            <TableRow key={record.id} data-state={selectedIds.has(record.id) && "selected"}>
                                <TableCell>
                                    <Checkbox
                                        checked={selectedIds.has(record.id)}
                                        onCheckedChange={(checked) => handleSelectOne(record.id, checked)}
                                    />
                                </TableCell>
                                <TableCell>
                                    {record.is_visible !== false ? 
                                        <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">Visible</Badge> : 
                                        <Badge variant="destructive">Hidden</Badge>
                                    }
                                </TableCell>
                                {columns.map(col => <TableCell key={col.key}>{record[col.key]}</TableCell>)}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {records.length === 0 && <p className="p-4 text-center text-sm text-gray-500">No records found for this entity.</p>}
            </ScrollArea>
        </div>
    );
}